<template lang="pug">

tl-accordion(
    :multi="true"
    class="tl-object-preview"
)
    template(
        v-for="(value, key) in data"
        :key="key"
    )
        template(
            v-if="value?.type === objectPreviewFilesKey"
        )
            .object__row(:data-testid="`row-${kebabCase(`${key}`)}`")
                .object__key(data-testid="key") {{ labels[`${key}`] || formatKey(`${key}`) }}
                .object__value(data-testid="value")
                    span(
                        class="object__value-file"
                        @click="handleDownloadFile(value)"
                    )
                        span {{ value.resourceName }}
        template(
            v-else-if="value !== null && typeof value === 'object' && !Object.keys(value || {})?.length"
        )
            .object__row(:data-testid="`row-${kebabCase(`${key}`)}`")
                .object__key(data-testid="key") {{ labels[`${key}`] || formatKey(`${key}`) }}
                .object__value(data-testid="value") &mdash;
                .object__type(data-testid="type") object

        tl-accordion-item(
            v-else-if="typeof value === 'object' && Object.keys(value || {})?.length"
            class="object__row object__row--no-hover"
            :data-testid="`row-${kebabCase(labels[`${key}`] || formatKey(`${key}`))}`"
        )
            template(#trigger)
                .object__key(data-testid="key") {{ labels[`${key}`] || formatKey(`${key}`) }}

            template(#content)
                .nested(data-testid="value")
                    nested-object(
                        :data="value"
                        :labels="labels"
                    )

        div(v-else)
            template(v-if="Array.isArray(value)")
                div(
                    v-for="(item, index) in value"
                    :key="index"
                    :data-testid="`row-${index}`"
                )
                    template(v-if="typeof item === 'object'")
                        nested-object(
                            :data="value"
                            :labels="labels"
                        )
                    div(v-else)
                        .object__value(data-testid="value") {{ value }}
            div(v-else)
                .object__row(:data-testid="`row-${typeof key === 'number' ? key : kebabCase(`${key}`)}`")
                    .object__key(data-testid="key") {{ labels[`${key}`] || formatKey(`${key}`) }}
                    .object__value(data-testid="value")
                        | {{ value === null ? 'null' : value.toString() || '&mdash;' }}
                    .object__type(data-testid="type") {{ value === null ? 'null' : typeof value }}
</template>

<script lang="ts">
import { defineComponent, inject, provide } from 'vue'
import { kebabCase, sentenceCase } from 'change-case'
import { useObjectPreview } from '@/components/$tl/utils/object-preview'
import type { ObjPreviewFileData } from '@/types'

export default defineComponent({
    name: 'NestedObject',
    props: {
        data:  {
            type: Object,
            default: () => ({ empty: 'true' }),
        },
        labels:  {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['download-file'],
    setup(_, { emit }) {
        const { objectPreviewFilesKey, downloadFileInjectionKey } = useObjectPreview()

        const formatKey = (key: string) => {
            return sentenceCase(`${key}`)
                .replace(/\bid\b/i, 'ID')
        }

        const types = ['string', 'number', 'boolean']

        const handleDownloadFileInjected = inject<((fileData: ObjPreviewFileData) => void) | null>(downloadFileInjectionKey, null)

        const handleDownloadFile = (fileData: ObjPreviewFileData) => {
            if (handleDownloadFileInjected) {
                handleDownloadFileInjected(fileData)
                return
            }

            emit('download-file', fileData)
        }

        if (!handleDownloadFileInjected) {
            provide(downloadFileInjectionKey, handleDownloadFile)
        }

        return {
            types,
            formatKey,
            objectPreviewFilesKey,
            handleDownloadFile,
            kebabCase,
        }
    },
})
</script>

<style lang="sass" scoped>
.tl-object-preview
    width: 100%
    overflow: hidden
    --label-padding: 6px
    .tl-accordion
        border-left: 1px solid var(--color-gray-tint-normal)

.nested
    padding-left: var(--padding-2)

.tl-accordion__item
    --padding-item: 0 !important
    --padding-trigger: 0 !important
    --padding-content: 0 !important
    border: none !important

.object
    &__row
        margin-bottom: 8px
        border-radius: var(--border-radius)
        padding: 2px
        padding-right: 0
        margin-left: 4px
        position: relative
        &:not(.object__row--no-hover):hover
            background-color: var(--color-gray-tint-normal)
            .object__type
                opacity: .5

        :deep(.tl-accordion__trigger)
            margin-bottom: 4px

        &.tl-accordion__item:last-child
            margin-bottom: 0
            padding-bottom: 0

    &__key
        display: block
        padding: var(--label-padding)
        word-break: keep-all
        font-size: 14px
        opacity: 0.5

    &__type
        position: absolute
        top: 12px
        right: 12px
        font-size: 12px
        opacity: 0

    &__value
        width: 100%
        overflow: auto
        font-weight: 500
        padding: var(--label-padding)

        &-file
            text-decoration: none
            color: inherit
            display: flex
            align-items: center
            cursor: pointer
            span
                flex: 1
            &:after
                content: "Download"
                color: var(--color-tl-blue)
                padding-left: 5px
                transition: all .2s
            &:hover
                &:after
                    text-decoration: underline

</style>
